/**
 * Agent encryption settings management for localStorage
 * Settings are stored per user+agent combination and persist forever
 */

export interface AgentEncryptionSettings {
  enabled: boolean
  key: string
}

export interface AgentOpenAISettings {
  enabled: boolean
  apiKey: string
}

const STORAGE_KEY_PREFIX = 'mazpin_agent_encryption_'
const OPENAI_STORAGE_KEY_PREFIX = 'mazpin_agent_openai_'
const API_KEY_STORAGE_PREFIX = 'mazpin_agent_apikey_'

/**
 * Gets the current user ID from localStorage or auth store
 * @returns The user ID or 'anonymous' if not logged in
 */
function getCurrentUserId(): string {
  try {
    // Try to get user from localStorage first
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user: unknown = JSON.parse(userStr)
      if (user && typeof user === 'object' && 'id' in user) {
        const typedUser = user as { id: unknown }
        if (typeof typedUser.id === 'string') {
          return typedUser.id
        }
      }
    }
    
    // Fallback to token-based identification or anonymous
    const token = localStorage.getItem('token')
    if (token) {
      // Use a hash of the token as user identifier if no user ID available
      return btoa(token).substring(0, 16)
    }
    
    return 'anonymous'
  } catch (error) {
    console.warn('Failed to get user ID for encryption settings:', error)
    return 'anonymous'
  }
}

/**
 * Generates storage key for user+agent combination
 * @param agentId The agent ID
 * @returns The storage key
 */
function getStorageKey(agentId: string): string {
  const userId = getCurrentUserId()
  return `${STORAGE_KEY_PREFIX}${userId}_${agentId}`
}

/**
 * Gets encryption settings for a specific agent for the current user
 * @param agentId The agent ID
 * @returns The encryption settings or default values
 */
export function getAgentEncryptionSettings(agentId: string): AgentEncryptionSettings {
  const storageKey = getStorageKey(agentId)
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed: unknown = JSON.parse(stored)
      if (typeof parsed === 'object' && parsed !== null) {
        const candidate = parsed as Partial<AgentEncryptionSettings>
        return {
          enabled: typeof candidate.enabled === 'boolean' ? candidate.enabled : false,
          key: typeof candidate.key === 'string' ? candidate.key : ''
        }
      }
    }
  } catch (error) {
    console.warn('Failed to parse agent encryption settings:', error)
  }
  
  // Return default settings
  return {
    enabled: false,
    key: ''
  }
}

/**
 * Saves encryption settings for a specific agent for the current user
 * @param agentId The agent ID
 * @param settings The encryption settings to save
 */
export function saveAgentEncryptionSettings(agentId: string, settings: AgentEncryptionSettings): void {
  const storageKey = getStorageKey(agentId)
  try {
    localStorage.setItem(storageKey, JSON.stringify(settings))
  } catch (error) {
    console.error('Failed to save agent encryption settings:', error)
    throw new Error('Failed to save encryption settings')
  }
}

/**
 * Gets all agent encryption settings for the current user
 * @returns Record of agent IDs to their encryption settings
 */
export function getAllAgentEncryptionSettings(): Record<string, AgentEncryptionSettings> {
  const settings: Record<string, AgentEncryptionSettings> = {}
  const userId = getCurrentUserId()
  const userPrefix = `${STORAGE_KEY_PREFIX}${userId}_`
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(userPrefix)) {
        const agentId = key.substring(userPrefix.length)
        const stored = localStorage.getItem(key)
        if (stored) {
          const parsed: unknown = JSON.parse(stored)
          if (typeof parsed === 'object' && parsed !== null) {
            const candidate = parsed as Partial<AgentEncryptionSettings>
            settings[agentId] = {
              enabled: typeof candidate.enabled === 'boolean' ? candidate.enabled : false,
              key: typeof candidate.key === 'string' ? candidate.key : ''
            }
          }
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load agent encryption settings:', error)
  }
  
  return settings
}

/**
 * Gets all encryption settings across all users (for debugging/admin purposes)
 * @returns Record of storage keys to their encryption settings
 */
export function getAllUsersAgentEncryptionSettings(): Record<string, AgentEncryptionSettings> {
  const settings: Record<string, AgentEncryptionSettings> = {}
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        const stored = localStorage.getItem(key)
        if (stored) {
          const parsed: unknown = JSON.parse(stored)
          if (typeof parsed === 'object' && parsed !== null) {
            const candidate = parsed as Partial<AgentEncryptionSettings>
            settings[key] = {
              enabled: typeof candidate.enabled === 'boolean' ? candidate.enabled : false,
              key: typeof candidate.key === 'string' ? candidate.key : ''
            }
          }
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load all agent encryption settings:', error)
  }
  
  return settings
}

/**
 * Generates storage key for OpenAI settings for user+agent combination
 * @param agentId The agent ID
 * @returns The storage key
 */
function getOpenAIStorageKey(agentId: string): string {
  const userId = getCurrentUserId()
  return `${OPENAI_STORAGE_KEY_PREFIX}${userId}_${agentId}`
}

/**
 * Gets OpenAI API key settings for a specific agent for the current user
 * @param agentId The agent ID
 * @returns The OpenAI settings or default values
 */
export function getAgentOpenAISettings(agentId: string): AgentOpenAISettings {
  const storageKey = getOpenAIStorageKey(agentId)
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed: unknown = JSON.parse(stored)
      if (typeof parsed === 'object' && parsed !== null) {
        const candidate = parsed as Partial<AgentOpenAISettings>
        return {
          enabled: typeof candidate.enabled === 'boolean' ? candidate.enabled : false,
          apiKey: typeof candidate.apiKey === 'string' ? candidate.apiKey : ''
        }
      }
    }
  } catch (error) {
    console.warn('Failed to parse agent OpenAI settings:', error)
  }
  
  // Return default settings
  return {
    enabled: false,
    apiKey: ''
  }
}

/**
 * Saves OpenAI API key settings for a specific agent for the current user
 * @param agentId The agent ID
 * @param settings The OpenAI settings to save
 */
export function saveAgentOpenAISettings(agentId: string, settings: AgentOpenAISettings): void {
  const storageKey = getOpenAIStorageKey(agentId)
  try {
    localStorage.setItem(storageKey, JSON.stringify(settings))
  } catch (error) {
    console.error('Failed to save agent OpenAI settings:', error)
    throw new Error('Failed to save OpenAI settings')
  }
}

/**
 * Gets all agent OpenAI settings for the current user
 * @returns Record of agent IDs to their OpenAI settings
 */
export function getAllAgentOpenAISettings(): Record<string, AgentOpenAISettings> {
  const settings: Record<string, AgentOpenAISettings> = {}
  const userId = getCurrentUserId()
  const userPrefix = `${OPENAI_STORAGE_KEY_PREFIX}${userId}_`
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(userPrefix)) {
        const agentId = key.substring(userPrefix.length)
        const stored = localStorage.getItem(key)
        if (stored) {
          const parsed: unknown = JSON.parse(stored)
          if (typeof parsed === 'object' && parsed !== null) {
            const candidate = parsed as Partial<AgentOpenAISettings>
            settings[agentId] = {
              enabled: typeof candidate.enabled === 'boolean' ? candidate.enabled : false,
              apiKey: typeof candidate.apiKey === 'string' ? candidate.apiKey : ''
            }
          }
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load agent OpenAI settings:', error)
  }
  
  return settings
}

/**
 * Generates storage key for agent API key for user+agent combination
 * @param agentId The agent ID
 * @returns The storage key
 */
function getApiKeyStorageKey(agentId: string): string {
  const userId = getCurrentUserId()
  return `${API_KEY_STORAGE_PREFIX}${userId}_${agentId}`
}

/**
 * Gets agent API key for a specific agent for the current user
 * @param agentId The agent ID
 * @returns The API key or empty string if not found
 */
export function getAgentApiKey(agentId: string): string {
  const storageKey = getApiKeyStorageKey(agentId)
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      return stored
    }
  } catch (error) {
    console.warn('Failed to get agent API key:', error)
  }
  
  return ''
}

/**
 * Saves agent API key for a specific agent for the current user
 * @param agentId The agent ID
 * @param apiKey The API key to save
 */
export function saveAgentApiKey(agentId: string, apiKey: string): void {
  const storageKey = getApiKeyStorageKey(agentId)
  try {
    if (apiKey) {
      localStorage.setItem(storageKey, apiKey)
    } else {
      // Remove the key if empty
      localStorage.removeItem(storageKey)
    }
  } catch (error) {
    console.error('Failed to save agent API key:', error)
    throw new Error('Failed to save API key')
  }
}

/**
 * Gets all agent API keys for the current user
 * @returns Record of agent IDs to their API keys
 */
export function getAllAgentApiKeys(): Record<string, string> {
  const apiKeys: Record<string, string> = {}
  const userId = getCurrentUserId()
  const userPrefix = `${API_KEY_STORAGE_PREFIX}${userId}_`
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(userPrefix)) {
        const agentId = key.substring(userPrefix.length)
        const stored = localStorage.getItem(key)
        if (stored) {
          apiKeys[agentId] = stored
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load agent API keys:', error)
  }
  
  return apiKeys
}